import type { ReactNode } from "react";
import { Link } from "react-router";

import { Logo } from "@/components/brand/Logo";
import { ContactEmail } from "@/components/ui/ContactEmail";
import { useLocale } from "@/i18n";

function Column({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h3 className="mb-4 text-sm font-medium text-cream-50">{heading}</h3>
      <ul className="space-y-3 text-sm text-cream-100/55">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="transition-colors hover:text-cream-50">
        {children}
      </Link>
    </li>
  );
}

export function Footer() {
  const { t, path } = useLocale();
  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-white/8 bg-ink-950">
      <div className="container-page py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="max-w-xs">
            {/* Footer is a dark surface — the light mark is used as supplied. */}
            <Logo size="md" alt={t.brand.logoAlt} />
            <p className="mt-5 text-sm leading-relaxed text-cream-100/55">
              {t.footer.tagline}
            </p>
          </div>

          <Column heading={t.footer.product}>
            <FooterLink to={path("/features")}>{t.nav.features}</FooterLink>
            <FooterLink to={path("/how-it-works")}>{t.nav.howItWorks}</FooterLink>
            <FooterLink to={path("/processing-modes")}>{t.nav.modes}</FooterLink>
            <FooterLink to={path("/pricing")}>{t.nav.pricing}</FooterLink>
          </Column>

          <Column heading={t.footer.company}>
            <FooterLink to={path("/security")}>{t.nav.security}</FooterLink>
            <FooterLink to={path("/contact")}>{t.nav.contact}</FooterLink>
            <li>
              <ContactEmail />
            </li>
          </Column>

          <Column heading={t.footer.legal}>
            {/*
              These are ATHKYA's own documents, written from what the product
              actually does — not adapted from the upstream project's, which
              belong to another company (see docs/ATHKYA_REBRAND_AUDIT.md §1).
              Both carry a visible notice that they await legal review.
            */}
            <FooterLink to={path("/privacy")}>{t.footer.privacy}</FooterLink>
            <FooterLink to={path("/terms")}>{t.footer.terms}</FooterLink>
          </Column>
        </div>

        <div className="mt-14 flex flex-col gap-3 border-t border-white/8 pt-8 text-sm text-cream-100/40 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} {t.brand.company}. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
}
